import { gql } from '@apollo/client'
import { Avatar, BodyText, Caption, Chip, Link, ReplyIcon } from '@farol-ds/react'
import {
  ARTIFACT_LABEL,
  mapToGA4FeedbackName,
  ProfileQuickview,
  relativeTimeFromNow,
  useDocumentModerationModal,
  useDocumentReportModal,
  useMetrics,
} from '@jusbrasil-web/docview/core'
import { LoginTrigger } from '@jusbrasil-web/shared-bifrost'
import { useGA4Dispatcher } from '@jusbrasil-web/shared-ga4'
import {
  COMPONENT_CLASS,
  FEEDBACK_CLASS,
  FEEDBACK_METHOD,
  GA4ClickEvent,
  GA4FeedbackGivenEvent,
  SELECTED_OPTION,
} from '@jusbrasil-web/shared-ga4-events'
import classNames from 'classnames'
import { useRef, useState } from 'react'

import { ACCESS_CONDITION_PUBLIC, NON_EXISTENT_PARENT_COMMENT_ID } from '../../constants'
import { PublishComment } from '../comments/components'
import {
  Comment_CommentFragment,
  Comment_DocumentFragment,
  Comment_ViewerFragment,
} from './__generated__/comment.graphql'
import styles from './comment.module.scss'
import { DropdownMoreActions, UpVoteButton } from './components'

type ProfileType = NonNullable<Comment_CommentFragment['profile']>

export interface CommentProps {
  comment: Comment_CommentFragment
  document: Comment_DocumentFragment
  viewer: Comment_ViewerFragment | null
}

export function Comment({ comment, viewer, document }: CommentProps) {
  const [isEditMode, setEditMode] = useState(false)
  const isParentComment = comment.parentCommentId === NON_EXISTENT_PARENT_COMMENT_ID
  const isPrivateComment = comment.accessCondition !== ACCESS_CONDITION_PUBLIC
  const review = comment.review
  const { replies = [] } = comment
  const { sendMetrics } = useMetrics<HTMLDivElement>()
  const documentReport = useDocumentReportModal()
  const documentModeration = useDocumentModerationModal()
  const profileQuickviewRef = useRef<HTMLDivElement>(null)
  const profileQuickviewAnchorRef = useRef<HTMLAnchorElement>(null)
  const { sendGA4Event } = useGA4Dispatcher()

  const profile = comment.profile as ProfileType
  const [isReplyCommentVisible, setReplyCommentVisible] = useState(false)

  const toggleEditMode = () => setEditMode(!isEditMode)
  const toggleReply = () => {
    isReplyCommentVisible ||
      sendGA4Event(
        new GA4ClickEvent({
          componentClass: COMPONENT_CLASS.DOCUMENT_FOOTER,
          componentName: null,
          selectedOption: SELECTED_OPTION.REPLY_COMMENT,
        })
      )
    setReplyCommentVisible(!isReplyCommentVisible)
  }

  const onReport = () => {
    sendMetrics('Doc.ReportClicked')
    documentReport.open({
      docId: comment.id,
      artifact: ARTIFACT_LABEL.COMENTARIO,
    })
  }

  const handleVoteClick = (onVoteClick: boolean) => {
    if (onVoteClick) {
      sendGA4Event(
        new GA4FeedbackGivenEvent({
          componentClass: COMPONENT_CLASS.DOCUMENT_FOOTER,
          resourceId: `${document.artifact}:${document.docId}`,
          feedbackClass: FEEDBACK_CLASS.COMMENT_FEEDBACK,
          feedbackName: mapToGA4FeedbackName(FEEDBACK_CLASS.COMMENT_FEEDBACK, document.artifact),
          feedbackMethod: FEEDBACK_METHOD.LIKE,
        })
      )
    }
  }

  const onModeration = () =>
    documentModeration.open({
      docId: comment.id,
      artifact: ARTIFACT_LABEL.COMENTARIO,
    })

  return (
    <div className={classNames({ [styles.commentFaded]: isPrivateComment })}>
      <div hidden={isEditMode}>
        <header className={styles.header}>
          <Link href={profile.url || ''}>
            <Avatar
              size="lg"
              className={styles.headerAvatar}
              src={profile.thumborImage?.current || ''}
              alt={profile.fullname || ''}
              loading="lazy"
              ref={profileQuickviewRef}
            />
          </Link>
          <span className={styles.headerInfo}>
            <Link
              type="ghost"
              className={styles.author}
              href={profile.url || ''}
              ref={profileQuickviewAnchorRef}
            >
              {profile.fullname}
            </Link>
            {profile.hasProBadge && (
              <Chip kind="secondary" size="sm" className={styles.proBadge}>
                Assinante
              </Chip>
            )}
            {review?.profile && (
              <Caption asChild>
                <em>
                  {' '}
                  (Revisado por{' '}
                  <Link type="secondary" href={review.profile.url || '#'}>
                    {review.profile.fullname}
                  </Link>
                  )
                </em>
              </Caption>
            )}
            <Caption>{relativeTimeFromNow(comment.date)}</Caption>
          </span>
          <ProfileQuickview
            profile={profile}
            getElementNodes={() =>
              profileQuickviewRef.current && profileQuickviewAnchorRef.current
                ? [profileQuickviewRef.current, profileQuickviewAnchorRef.current]
                : []
            }
          />
        </header>
        <BodyText size="md" className={styles.content} asChild>
          <p dangerouslySetInnerHTML={{ __html: comment.text }} />
        </BodyText>
        <div className={styles.footer}>
          <LoginTrigger
            isLogged={!!viewer}
            options={{
              eventData: {
                from_component: 'docview/Comment',
              },
            }}
          >
            <div className={styles.mainAction}>
              <UpVoteButton comment={comment} viewer={viewer} onVoteClick={handleVoteClick} />
              {isParentComment && (
                <>
                  <BodyText size="md">•</BodyText>
                  <Link type="tertiary" className={styles.replyAction} asChild>
                    <button onClick={toggleReply}>
                      <ReplyIcon size="sm" />
                      <BodyText size="md">Responder</BodyText>
                    </button>
                  </Link>
                </>
              )}
            </div>
          </LoginTrigger>
          <DropdownMoreActions
            documentReport={documentReport}
            documentModeration={documentModeration}
            onReport={onReport}
            onModeration={onModeration}
            onEdit={toggleEditMode}
            document={document}
            comment={comment}
            viewer={viewer}
          />
        </div>
      </div>
      {isEditMode && (
        <PublishComment
          document={document}
          viewer={viewer}
          comment={comment}
          isEditMode={isEditMode}
          onButtonClick={toggleEditMode}
        />
      )}
      {isReplyCommentVisible && (
        <div className={styles.reply}>
          <PublishComment
            document={document}
            viewer={viewer}
            comment={comment}
            isReply
            onButtonClick={toggleReply}
          />
        </div>
      )}
      {replies.map((reply) => (
        <div key={reply.id} className={styles.reply}>
          <Comment comment={reply as Comment_CommentFragment} document={document} viewer={viewer} />
        </div>
      ))}
    </div>
  )
}

Comment.fragments = {
  comment: gql`
    fragment Comment_comment on DocumentComment {
      id
      text
      date
      profile {
        url
        fullname
        thumborImage {
          current: thumbUrl(size: "50x50")
        }
        hasProBadge
        ...ProfileQuickview_profile
      }
      parentCommentId
      accessCondition
      review {
        profile {
          fullname
          url
        }
      }
      ...UpVoteButton_comment
      ...DropdownMoreActions_comment
      ...PublishComment_comment
      replies {
        id
        text
        date
        profile {
          url
          fullname
          thumborImage {
            current: thumbUrl(size: "50x50")
          }
          hasProBadge
          ...ProfileQuickview_profile
        }
        parentCommentId
        accessCondition
        review {
          profile {
            fullname
            url
          }
        }
        ...UpVoteButton_comment
        ...DropdownMoreActions_comment
        ...PublishComment_comment
      }
    }
    ${UpVoteButton.fragments.comment}
    ${DropdownMoreActions.fragments.comment}
    ${PublishComment.fragments.comment}
    ${ProfileQuickview.fragments.profile}
  `,
  document: gql`
    fragment Comment_document on Document {
      ...PublishComment_document
      ...DropdownMoreActions_document
    }
    ${PublishComment.fragments.document}
    ${DropdownMoreActions.fragments.document}
  `,
  viewer: gql`
    fragment Comment_viewer on Profile {
      ...PublishComment_viewer
      ...UpVoteButton_viewer
      ...DropdownMoreActions_viewer
    }
    ${PublishComment.fragments.viewer}
    ${UpVoteButton.fragments.viewer}
    ${DropdownMoreActions.fragments.viewer}
  `,
}
